import {
  HfsMissionSettingsActionEnum,
  type MowerAreaSchedule,
  MowerAreaType,
  MowerAreaTypeSetting,
  type MowerScheduleTask,
  MissionTypeEnum as HfsMissionTypeEnum,
  MowerScheduleSetting,
  type MowerAreaAugmentByVisionSetting,
} from 'fleet-data-model';
import {
  type AssetOperationExecutionTarget,
  dayNames,
  RoboticMowerSettingsResponse,
  type StoredAssetHfsAction,
  type TaskList,
} from '@fleet-bffs/api-contract';
import {
  type MissionAugmentByVisionState,
  MissionCuttingHeightState,
  MissionEnabledState,
  MissionScheduleState,
  MissionSettingsOperationPayload,
  MowerCuttingHeightState,
  MowerScheduleState,
  MowingBehaviourSettingsState,
} from '~/domain/assets/store/hfs-operations/reducer';
import { getHfsOperationPayloadMemoTimeout } from '~/domain/assets/utils/hfs-operation-timeouts';
import type { MissionSettingsAvailability } from '~/domain/roboticmower-details/models/MissionSettingsAvailability';
import { availableRoboticMowersPatterns } from '~/components/containers/PatternSelectorContainer/selectors';
import { BorderMowingType, MowingMode, MowingPriority } from '~/data/Asset';
import { OperationStatus } from '~/domain/assets/models/OperationStatus';
import type { AreaBehaviourSettings } from '~/components/containers/PatternSelectorContainer/types';
import type { IconName } from '~uikit/Icon/icon-names';
import type { MissionUpdateParams } from '~/api/pattern-selector-api';
import { UpdateMissionTypeEnum } from '~/domain/roboticmower-details/models/UpdateMissionTypeEnum';
import { Operation } from '~/domain/assets/models';
import {
  checkIfOperationIsActive,
  checkIfOperationIsFailed,
} from '~/domain/assets/utils/hfs-operations-utils';

// Time that it might take to have changes propagated after operation completion
const getPropagationOffsetSec = (actionId: UpdateMissionTypeEnum) =>
  getHfsOperationPayloadMemoTimeout(actionId) / 1000;

// In some cases we might want to handle REJECTED outside of common indication
export const missionUpdateFailuresIndicationMap = [
  {
    mobileView: true,
    type: UpdateMissionTypeEnum.UpdateMissionBehaviour,
    statuses: [OperationStatus.Timeout, OperationStatus.Failed, OperationStatus.Error],
  },
  {
    type: UpdateMissionTypeEnum.UpdateMissionEnabled,
    statuses: [
      OperationStatus.Timeout,
      OperationStatus.Failed,
      OperationStatus.Error,
      OperationStatus.Rejected,
    ],
  },
  {
    type: UpdateMissionTypeEnum.UpdateMissionAugmentByVision,
    statuses: [
      OperationStatus.Timeout,
      OperationStatus.Failed,
      OperationStatus.Error,
      OperationStatus.Rejected,
    ],
  },
];

export const filterRelevantPayloadItems = (
  items: MissionSettingsOperationPayload[],
  actionId: UpdateMissionTypeEnum
) => {
  const now = new Date();
  return items.filter((item) => {
    if (item.completionDate !== undefined) {
      const diff = (now.getTime() - item.completionDate.getTime()) / 1000;
      if (diff > getPropagationOffsetSec(actionId)) {
        // Operation has been completed for too long time now, we can consider changes should take effect by now
        return false;
      }
    }
    return true;
  });
};

export const getCleanupTimeout = (
  item: MissionSettingsOperationPayload,
  actionId: UpdateMissionTypeEnum
) => {
  const now = new Date();
  if (item.completionDate !== undefined) {
    const refetchTime = item.completionDate.getTime() + getPropagationOffsetSec(actionId) * 1000;
    return refetchTime - now.getTime();
  }
  return -1;
};

export const getRefetchTimeout = (
  item: MissionSettingsOperationPayload,
  actionId: UpdateMissionTypeEnum
) => {
  if (item.completionDate !== undefined) {
    return getCleanupTimeout(item, actionId) - 5000;
  }
  return -1;
};

export const filterMissionPayloadItems = (
  items: MissionSettingsOperationPayload[],
  iprId: string,
  missionId: number
) => items.filter((item) => item.iprId === iprId && missionId === item.missionId);

export const mapMissionSettingsAvailabilityFromHfsActions = (
  response: RoboticMowerSettingsResponse | undefined,
  missionId: number
): MissionSettingsAvailability => {
  const actions =
    response?.hfsActions?.filter((action) => Number(action.metadata?.missionId) === missionId) ??
    [];
  const areaTypeAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_TYPE
  ) as undefined | MowerAreaType;
  const raiseDiskOnTurnAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_RAISE_DISK_ON_TURN
  );
  const borderMowingEnabledAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_BORDER_MOWING_ENABLED
  );
  const borderMowingFixedAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_BORDER_MOWING_FIXED
  );
  const systematicMissionCapable =
    (
      areaTypeAction?.settingData as undefined | MowerAreaTypeSetting
    )?.metadata?.availableValues?.includes(HfsMissionTypeEnum.SYSTEMATIC) ?? false;
  const randomMissionCapable = (
    areaTypeAction?.settingData as undefined | MowerAreaTypeSetting
  )?.metadata?.availableValues?.includes(HfsMissionTypeEnum.RANDOM);
  const raiseHeightOnTurnEnabledAvailable = raiseDiskOnTurnAction !== undefined;
  const borderMowingEnabledAvailable = borderMowingEnabledAction !== undefined;
  const borderMowingFixedAvailable = borderMowingFixedAction !== undefined;

  return {
    available: systematicMissionCapable,
    randomMissionCapable,
    raiseHeightOnTurnEnabledAvailable,
    borderMowingEnabledAvailable,
    borderMowingFixedAvailable,
  };
};

export const getValidMissionSettingsHfsActions = (
  response: RoboticMowerSettingsResponse | undefined,
  missionId: number
) => {
  const actionsTypes = new Set([
    HfsMissionSettingsActionEnum.MOWER_AREA_PATTERN,
    HfsMissionSettingsActionEnum.MOWER_AREA_BORDER_MOWING_ENABLED,
    HfsMissionSettingsActionEnum.MOWER_AREA_BORDER_MOWING_FIXED,
    HfsMissionSettingsActionEnum.MOWER_AREA_MOWING_PRIORITY,
    HfsMissionSettingsActionEnum.MOWER_AREA_TYPE,
    HfsMissionSettingsActionEnum.MOWER_AREA_MOWING_ORIENTATION,
    HfsMissionSettingsActionEnum.MOWER_AREA_RAISE_DISK_ON_TURN,
  ]);
  return (
    response?.hfsActions?.filter(
      (action) =>
        action.valid &&
        action.metadata?.missionId == missionId &&
        actionsTypes.has(action.hfsAction as HfsMissionSettingsActionEnum)
    ) ?? []
  );
};

export const mapMissionSettingsFromHfsActions = (
  response: RoboticMowerSettingsResponse | undefined,
  missionId: number,
  recentUpdate?: MowingBehaviourSettingsState
) => {
  const actions =
    response?.hfsActions?.filter((action) => Number(action.metadata?.missionId) == missionId) ?? [];
  const areaTypeAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_TYPE
  );
  const raiseDiskOnTurnAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_RAISE_DISK_ON_TURN
  );
  const orientationAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_MOWING_ORIENTATION
  );
  const priorityAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_MOWING_PRIORITY
  );
  const patternAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_PATTERN
  );
  const borderMowingEnabledAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_BORDER_MOWING_ENABLED
  );
  const borderMowingFixedAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_BORDER_MOWING_FIXED
  );
  const orientationShift = recentUpdate?.orientationShift ?? patternAction?.settingData?.value;
  const pattern = availableRoboticMowersPatterns.find(
    (item) => item.orientationShift === orientationShift
  );
  const recentUpdateType = recentUpdate?.type === MowingMode.SYSTEMATIC ? 'SYSTEMATIC' : 'RANDOM';
  const type = recentUpdate === undefined ? areaTypeAction?.settingData?.value : recentUpdateType;
  const mowingPriority = (recentUpdate?.mowingPriority ??
    priorityAction?.settingData?.value) as MowingPriority;
  const orientation = recentUpdate?.orientation ?? orientationAction?.settingData?.value;
  const raiseHeightOnTurnEnabled = !!(
    recentUpdate?.raiseHeightOnTurnEnabled ?? raiseDiskOnTurnAction?.settingData?.value
  );
  const hfsActionBorderMowingType = borderMowingFixedAction?.settingData?.value
    ? BorderMowingType.FIXED
    : BorderMowingType.VARIABLE;

  return {
    borderMowingType: recentUpdate?.borderMowingType ?? hfsActionBorderMowingType,
    cuttingPatternIconType: pattern?.cuttingPatternIconType,
    cuttingPatternTranslationKey: pattern?.cuttingPatternTranslationKey,
    mowingMode: type === HfsMissionTypeEnum.SYSTEMATIC ? MowingMode.SYSTEMATIC : MowingMode.RANDOM,
    mowingPriority,
    orientation: Number(orientation ?? 0),
    orientationShift: Number(orientationShift ?? 0),
    raiseHeightOnTurnEnabled,
    borderMowingEnabled:
      recentUpdate?.borderMowingEnabled ?? borderMowingEnabledAction?.settingData?.value,
  };
};

export const mapMissionEnabledStatusFromHfsActions = (
  response: RoboticMowerSettingsResponse | undefined,
  missionId: number,
  recentUpdate?: MissionEnabledState
) => {
  const actions =
    response?.hfsActions?.filter((action) => Number(action.metadata?.missionId) == missionId) ?? [];
  const enabledAction = actions.find(
    (action) => action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_ENABLED
  );

  return recentUpdate?.enabled ?? enabledAction?.settingData?.value;
};

/** The mission's MOWER_AREA_AUGMENT_BY_VISION action, or undefined when the area has none. */
export const findMissionAugmentByVisionAction = (
  response: RoboticMowerSettingsResponse | undefined,
  missionId: number
) =>
  response?.hfsActions?.find(
    (action) =>
      action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_AUGMENT_BY_VISION &&
      Number(action.metadata?.missionId) == missionId
  );

/** Vision assisted navigation: a pending local change wins over the reported value. */
export const mapMissionAugmentByVisionFromHfsActions = (
  response: RoboticMowerSettingsResponse | undefined,
  missionId: number,
  recentUpdate?: MissionAugmentByVisionState
): boolean | undefined => {
  if (recentUpdate !== undefined) {
    return recentUpdate.augmentByVisionEnabled;
  }
  const settingData = findMissionAugmentByVisionAction(response, missionId)?.settingData as
    | MowerAreaAugmentByVisionSetting
    | undefined;
  return settingData?.value?.augmentByVisionEnabled;
};

export const mapPatternIconFromMissionSettings = (
  settings: Pick<AreaBehaviourSettings, 'mowingMode' | 'cuttingPatternIconType'>
) => {
  return settings.mowingMode === MowingMode.SYSTEMATIC && settings.cuttingPatternIconType
    ? (`pattern_${settings.cuttingPatternIconType}` as IconName)
    : 'pattern_random';
};

export const createMissionChangeset = (update: MissionUpdateParams, iprId: string) => {
  const systematic = update.type === MowingMode.SYSTEMATIC;
  const changeset: AssetOperationExecutionTarget[] = [
    {
      iprId,
      hfsActionId: HfsMissionSettingsActionEnum.MOWER_AREA_TYPE,
      payload: {
        type: systematic ? 'SYSTEMATIC' : 'RANDOM',
        missionId: update.missionId,
      },
    },
  ];
  if (systematic) {
    changeset.push(
      {
        iprId,
        hfsActionId: HfsMissionSettingsActionEnum.MOWER_AREA_PATTERN,
        payload: {
          orientationShift: update.orientationShift,
          missionId: update.missionId,
        },
      },
      {
        iprId,
        hfsActionId: HfsMissionSettingsActionEnum.MOWER_AREA_MOWING_ORIENTATION,
        payload: {
          orientation: update.orientation,
          missionId: update.missionId,
        },
      }
    );
    if (update.raiseHeightOnTurnEnabled !== undefined) {
      changeset.push({
        iprId,
        hfsActionId: HfsMissionSettingsActionEnum.MOWER_AREA_RAISE_DISK_ON_TURN,
        payload: {
          raiseHeightOnTurnEnabled: update.raiseHeightOnTurnEnabled,
          missionId: update.missionId,
        },
      });
    }
    if (update.borderMowingEnabled !== undefined) {
      changeset.push({
        iprId,
        hfsActionId: HfsMissionSettingsActionEnum.MOWER_AREA_BORDER_MOWING_ENABLED,
        payload: {
          boundaryMowingEnabled: update.borderMowingEnabled,
          missionId: update.missionId,
        },
      });
    }
    changeset.push(
      {
        iprId,
        hfsActionId: HfsMissionSettingsActionEnum.MOWER_AREA_BORDER_MOWING_FIXED,
        payload: {
          fixedBoundary: update.borderMowingType === BorderMowingType.FIXED,
          missionId: update.missionId,
        },
      },
      {
        iprId,
        hfsActionId: HfsMissionSettingsActionEnum.MOWER_AREA_MOWING_PRIORITY,
        payload: {
          mowingPriority: update.mowingPriority,
          missionId: update.missionId,
        },
      }
    );
  }
  return changeset;
};

/*
 * Returns array containing all schedules for all missions that can be found in given aray of asset hfsActions for given iprId.
 * */
export const getHfsActionsAreasScheduleEntries = (
  iprId: string,
  hfsActions: StoredAssetHfsAction[]
) => {
  const scheduleHfsActions = hfsActions.filter(
    (action) =>
      action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_SCHEDULE &&
      action.iprId === iprId &&
      action.metadata?.missionId !== undefined
  ) as MowerAreaSchedule[];
  return scheduleHfsActions.map((action) => ({
    missionId: action.metadata.missionId,
    tasks: (action.settingData as MowerScheduleSetting | undefined)?.value ?? [],
  }));
};

export const getMissionHfsActionTaskList = (
  iprId: string,
  hfsActions: StoredAssetHfsAction[],
  missionId: number
) => {
  const hfsAction = hfsActions
    .filter((action) => action.iprId === iprId)
    .find(
      (action) =>
        action.hfsAction === HfsMissionSettingsActionEnum.MOWER_AREA_SCHEDULE &&
        action.metadata?.missionId === missionId
    );
  const settingData = hfsAction?.settingData as MowerScheduleSetting | undefined;
  return settingData?.value ?? [];
};

export const mapScheduleTasksToWeekList = (
  tasks: MowerScheduleTask[],
  missionId: number | undefined
): TaskList => {
  const result: TaskList = {};
  dayNames.forEach((dayCode) => {
    result[dayCode] = tasks
      .filter(
        (task) =>
          task.useOnDays[dayCode.toLowerCase()] === true &&
          task.start !== undefined &&
          task.duration !== undefined
      )
      .map((task) => ({
        missionId,
        id: task.taskId?.toString(),
        startTime: task.start! / 60,
        endTime: (task.start! + task.duration!) / 60,
      }));
  });
  return result;
};

export const mapMissionHfsActionSchedule = (
  iprId: string,
  hfsActions: StoredAssetHfsAction[],
  missionId: number
): TaskList => {
  const tasks = getMissionHfsActionTaskList(iprId, hfsActions, missionId);
  return mapScheduleTasksToWeekList(tasks, missionId);
};

export const mapWeekListToScheduleTasks = (
  taskList: TaskList,
  otherTasksIds: number[]
): MowerScheduleTask[] => {
  const taskMap = new Map<string, MowerScheduleTask>();
  const reservedTasksIds = [...otherTasksIds];
  dayNames.forEach((dayCode) => {
    const dayTasks = taskList[dayCode] ?? [];
    const dayKey = dayCode.toLowerCase() as keyof MowerScheduleTask['useOnDays'];

    dayTasks.forEach((task) => {
      const timeKey = `${task.startTime}:${task.endTime}`;
      const existing = taskMap.get(timeKey);

      if (existing) {
        existing.useOnDays[dayKey] = true;
      } else {
        let taskId = 0;
        while (reservedTasksIds.includes(taskId)) {
          taskId++;
        }
        reservedTasksIds.push(taskId);
        taskMap.set(timeKey, {
          taskId,
          start: task.startTime * 60,
          duration: (task.endTime - task.startTime) * 60,
          useOnDays: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
            [dayKey]: true,
          },
        });
      }
    });
  });

  return Array.from(taskMap.values());
};

export const getIsMissionOperationFailureIndicationNeeded = (
  operation: Operation,
  mobileView: boolean
) => {
  const failureStatuses =
    missionUpdateFailuresIndicationMap.find(
      (item) =>
        item.type === operation.hfsActionId &&
        (item.mobileView === mobileView || item.mobileView === undefined)
    )?.statuses ?? [];
  return failureStatuses.includes(operation.status);
};

export const getMissionOperationSuccessUpdate = (
  operation: Operation,
  mowingBehaviourSettings: MowingBehaviourSettingsState[],
  missionCuttingHeight: MissionCuttingHeightState[],
  missionSchedule: MissionScheduleState[]
) => {
  if (
    operation.operationId &&
    !checkIfOperationIsActive(operation) &&
    !checkIfOperationIsFailed(operation)
  ) {
    const mowingBehaviourSettingUpdate = mowingBehaviourSettings.find(
      (update) => update.operationId === operation.operationId
    );
    if (mowingBehaviourSettingUpdate) {
      return mowingBehaviourSettingUpdate;
    }
    const missionCuttingHeightUpdate = missionCuttingHeight.find(
      (update) => update.operationId === operation.operationId
    );
    if (missionCuttingHeightUpdate) {
      return missionCuttingHeightUpdate;
    }
    const missionScheduleUpdate = missionSchedule.find(
      (update) => update.operationId === operation.operationId
    );
    if (missionScheduleUpdate) {
      return missionScheduleUpdate;
    }
  }
  return undefined;
};

export const getMowerOperationSuccessUpdate = (
  operation: Operation,
  cuttingHeight: MowerCuttingHeightState[],
  schedule: MowerScheduleState[]
) => {
  if (
    operation.operationId &&
    !checkIfOperationIsActive(operation) &&
    !checkIfOperationIsFailed(operation)
  ) {
    const cuttingHeightUpdate = cuttingHeight.find(
      (update) => update.operationId === operation.operationId
    );
    if (cuttingHeightUpdate) {
      return cuttingHeightUpdate;
    }
    const scheduleUpdate = schedule.find((update) => update.operationId === operation.operationId);
    if (scheduleUpdate) {
      return scheduleUpdate;
    }
  }
  return undefined;
};
